import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import type { Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { merge } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import type {
  EvaluationPanelData,
  QuickEvaluationFormValue,
  TemplateEvaluationFormValue,
} from '@recruitee/evaluations-types';

import { EvaluationDraftService } from './evaluation-draft.service';
import { QuickEvaluationFormService } from './quick-evaluation-form.service';
import { TemplateEvaluationFormService } from './template-evaluation-form.service';

type RestoreValue = QuickEvaluationFormValue | TemplateEvaluationFormValue | null;

@Injectable()
export class EvaluationPanelDraftAutosaveService {
  private readonly quickForm = inject(QuickEvaluationFormService);
  private readonly templateForm = inject(TemplateEvaluationFormService);
  private readonly draftService = inject(EvaluationDraftService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly draftSaved = signal(false);
  public readonly restoreApplied = signal(false);

  private data!: Signal<EvaluationPanelData>;
  private restore!: Signal<RestoreValue>;

  public connect(data: Signal<EvaluationPanelData>, restore: Signal<RestoreValue>): void {
    this.data = data;
    this.restore = restore;

    merge(this.quickForm.form.valueChanges, this.templateForm.form.valueChanges)
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.persistDraft());

    this.destroyRef.onDestroy(() => this.persistDraft());
  }

  public reset(): void {
    this.draftSaved.set(false);
  }

  public applyRestore(): void {
    const restore = this.restore();
    if (this.restoreApplied() || !restore) return;
    this.restoreApplied.set(true);

    if (this.data().quickEvaluation) {
      this.quickForm.restoreDraft(<QuickEvaluationFormValue>restore);
    } else {
      this.templateForm.restoreDraft(<TemplateEvaluationFormValue>restore);
    }

    this.draftSaved.set(true);
  }

  private persistDraft(): void {
    const data = this.data();
    const form = data.quickEvaluation ? this.quickForm.form : this.templateForm.form;

    if (!form.dirty) {
      this.draftSaved.set(false);
      return;
    }

    this.draftService.saveDraft({
      data,
      form: <QuickEvaluationFormValue | TemplateEvaluationFormValue>form.getRawValue(),
      savedAt: Date.now(),
    });
    this.draftSaved.set(true);
  }
}
